import React, { useState, useEffect, useRef } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "./Breadcrumb";
import Swal from "sweetalert2";
import { API_URL } from "..";

const AddCompanyPage = () => {
  const [companies, setCompanies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCompanyId, setCurrentCompanyId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    vatNo: "",
    companyNo:"",
    logo: null,
    accountTitle: "",
    sortCode: "",
    accountNumber: "",
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [error, setError] = useState("");
  const [tableError, setTableError] = useState("");
  
  const fileInputRef = useRef(null);

  // Fetch companies
  const fetchCompanies = async () => {
    try {
      const headerList = {
        Accept: "*/*",
        "Content-Type": "application/json",
      };
      const response = await fetch(`${API_URL}/api/company`, {
        method: "GET",
        headers: headerList,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (response.status === 200) {
        if (!Array.isArray(data.companies)) {
          throw new Error("Invalid data format: companies is not an array");
        }
        setCompanies(data.companies || []);
        setTableError("");
      } else {
        throw new Error(data.message || "Failed to fetch companies");
      }
    } catch (err) {
      console.error("Fetch companies error:", err);
      setTableError(err.message || "Unable to load companies. Please try again.");
      setCompanies([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Unable to fetch companies",
      });
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  // Handle logo file change
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!["image/png", "image/jpeg"].includes(file.type)) {
        setError("Please upload a PNG or JPEG image");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setError("Image size must be less than 2MB");
        return;
      }
      setFormData((prev) => ({ ...prev, logo: file }));
      setLogoPreview(URL.createObjectURL(file));
      setError("");
    }
  };

  // Validate form
  const validateForm = () => {
    if (!formData.name.trim()) return "Company name is required";
    if (!formData.phone.trim()) return "Phone number is required";
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      return "Valid email is required";
    if (!formData.address.trim()) return "Address is required";
    if (!formData.vatNo.trim()) return "VAT number is required";
    if (!formData.companyNo.trim()) return "Company number is required";
    if (!isEditing && !formData.logo) return "Company logo is required";
    if (!formData.accountTitle.trim()) return "Account title is required";
    if (!formData.sortCode.trim()) return "Sort code is required";
    if (!formData.accountNumber.trim()) return "Account number is required";
    return "";
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("address", formData.address);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("companyNo", formData.companyNo);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("vat", formData.vatNo);
      formDataToSend.append("accountTitle", formData.accountTitle);
      formDataToSend.append("sortCode", formData.sortCode);
      formDataToSend.append("accountNumber", formData.accountNumber);
      if (formData.logo) formDataToSend.append("logo", formData.logo);

      const response = await fetch(
        `${API_URL}/api/company${isEditing ? `/${currentCompanyId}` : ""}`,
        {
          method: isEditing ? "PUT" : "POST",
          body: formDataToSend,
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          icon: "success",
          title: isEditing ? "Company Updated" : "Company Added",
          text: `Company Information has been ${
            isEditing ? "updated" : "added"
          } successfully`,
        });
        if (isEditing) {
          setCompanies((prev) =>
            prev.map((company) =>
              company.id === currentCompanyId
                ? {
                    ...company,
                    name: formData.name,
                    address: formData.address,
                    phone: formData.phone,
                    email: formData.email,
                    vat: formData.vatNo,
                    accountTitle: formData.accountTitle,
                    sortCode: formData.sortCode,
                    companyNo: formData.companyNo,
                    accountNumber: formData.accountNumber,
                    logo: data.company.logo || company.logo,
                  }
                : company
            )
          );
        } else {
          setCompanies((prev) => [...prev, data.company]);
        }
        resetForm();
      } else {
        throw new Error(data.message || "Operation failed");
      }
    } catch (err) {
      console.error("Submit error:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Unable to save company",
      });
    }
  };

  // Handle status change
  const handleStatusChange = async (id, status) => {
    try {
      const response = await fetch(`${API_URL}/api/company/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (response.status === 200) {
        setCompanies((prev) =>
          prev.map((company) =>
            company.id === id ? { ...company, status } : company
          )
        );
      } else {
        throw new Error(data.message || "Failed to update status");
      }
    } catch (err) {
      console.error("Status change error:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Unable to update status",
      });
    }
  };

  // Handle edit
  const handleEdit = (company) => {
    setIsEditing(true);
    setCurrentCompanyId(company.id);
    setFormData({
      name: company.name || "",
      address: company.address || "",
      phone: company.phone || "",
      email: company.email || "",
      vatNo: company.vat || company.vatNo || "",
      accountTitle: company.accountTitle || "",
      sortCode: company.sortCode || "",
      companyNo: company.companyNo || "",
      accountNumber: company.accountNumber || "",
      logo: null,
    });
    setLogoPreview(company.logo ? `${API_URL}${company.logo}` : null);
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_URL}/api/company/${id}`, {
          method: "DELETE",
        });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        if (response.status === 200) {
          Swal.fire("Deleted!", "Company has been deleted.", "success");
          setCompanies((prev) => prev.filter((company) => company.id !== id));
        } else {
          throw new Error(data.message || "Failed to delete company");
        }
      } catch (err) {
        console.error("Delete error:", err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err.message || "Unable to delete company",
        });
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      address: "",
      phone: "",
      email: "",
      vatNo: "",
      logo: null,
      accountTitle: "",
      sortCode: "",
      accountNumber: "",
      companyNo:'',
    });
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
    setShowModal(false);
    setIsEditing(false);
    setCurrentCompanyId(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Fetch companies on mount
  useEffect(() => {
    fetchCompanies();
  }, []);

  return (
    <>
      <MasterLayout>
        <Breadcrumb title="Companies" />

        <div className="card basic-data-table">
          <div className="card-header">
            <div className="row">
              <h5 className="card-title mb-0 col-md-6">List of Companies</h5>
              <div className="col-md-6 text-end">
                <button
                  className="btn btn-primary"
                  style={{ background: "#db6f2f", borderColor: "#db6f2f" }}
                  onClick={() => setShowModal(true)}
                >
                  Add New
                </button>
              </div>
            </div>
          </div>
          <div className="card-body">
            {tableError && (
              <div className="alert alert-danger mb-3">{tableError}</div>
            )}
            <table className="table bordered-table mb-0">
              <thead>
                <tr>
                  <th scope="col">Logo</th>
                  <th scope="col">Name</th>
                  <th scope="col">Phone</th>
                  <th scope="col">Status</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company?.id}>
                    <td>
                      <img
                        src={
                          company?.logo
                            ? `${API_URL}${company?.logo}`
                            : "/placeholder.png"
                        }
                        alt="Logo"
                        style={{ width: "100px", height: "100px", objectFit: 'contain' }}
                      />
                    </td>
                    <td>{company?.name}</td>
                    <td>{company?.phone}</td>
                    <td>
                      <select
                        className="status-select"
                        value={company.status}
                        onChange={(e) =>
                          handleStatusChange( company.id, e.target.value)
                        }
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </td>
                    <td>
                      <button
                        className="btn btn-primary me-2"
                        onClick={() => handleEdit(company)}
                      >
                        <i className="ph ph-pencil">Edit</i>
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(company.id)}
                      >
                        <i className="ph ph-trash">Delete</i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {companies.length === 0 && !tableError && (
              <div className="text-center mt-3">
                <p>No companies found. Add a new company to get started.</p>
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        {showModal && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {isEditing ? "Edit Company" : "Add New Company"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={resetForm}
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    {error && (
                      <div className="alert alert-danger">{error}</div>
                    )}
                    <div className="mb-3 text-center">
                      <div className="mb-2">
                        {logoPreview ? (
                          <img
                            src={logoPreview}
                            alt="Logo Preview"
                            style={{
                              maxWidth: "150px",
                              maxHeight: "150px",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "150px",
                              height: "150px",
                              background: "#f0f0f0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <span>No Logo</span>
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={handleLogoChange}
                        ref={fileInputRef}
                        className="form-control"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Company Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Address</label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      ></textarea>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">VAT Number</label>
                      <input
                        type="text"
                        name="vatNo"
                        value={formData.vatNo}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Company Number</label>
                      <input
                        type="text"
                        name="companyNo"
                        value={formData.companyNo}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <h5 className="mb-3">Payment Information</h5>
                    <div className="mb-3">
                      <label className="form-label">Account Title</label>
                      <input
                        type="text"
                        name="accountTitle"
                        value={formData.accountTitle}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Sort Code</label>
                      <input
                        type="text"
                        name="sortCode"
                        value={formData.sortCode}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Account Number</label>
                      <input
                        type="text"
                        name="accountNumber"
                        value={formData.accountNumber}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={resetForm}
                    >
                      Close
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {isEditing ? "Update" : "Save"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </MasterLayout>
    </>
  );
};

export default AddCompanyPage;